import { ref, computed, inject, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { usePriceStore } from '@/stores/price-store.js';


export function useProductPrice(priceAttributes, props) {
  const { $translate } = inject('i18n');
  
  /** This is to prevent the wrong price on product with attributes 
     * since attributes adds cost to product */
  const intialPriceLoaded = ref(false);

  // Price attributes
  const productConfiguratorPrice = ref(null);
  const price = ref(priceAttributes.price);
  const priceRaw = ref(priceAttributes.price_raw);
  const priceSpecial = ref(priceAttributes.price_special);
  const priceSpecialRaw = ref(priceAttributes.price_special_raw);
  const pricePerUnit = ref(priceAttributes.price_per_unit);
  const priceDiscountPercentage = ref(priceAttributes.price_discount_percentage);
  const tax = ref(priceAttributes.tax);
  const finalPriceExVatRaw = ref(priceAttributes.final_price_ex_vat_raw);
  const priceStore = usePriceStore();
  const loggedPrice = ref(null);
  const handlePriceLog = ref(props.handlePriceLogUpdate);
  const { loggedPrices } = storeToRefs(priceStore);
  const comparisonFactor = ref(priceAttributes.comparison_factor);
  const cheapestVariantPrice = ref(priceAttributes.cheapest_variant_price);
  const cheapestVariantPriceRaw = ref(priceAttributes.cheapest_variant_price_raw); // test again

  const getLoggedPrice = async () => {
    const productId = props.productData.id;
    await priceStore.getLoggedPrices([productId]);
    const product = priceStore.findLoggedPrice(productId);
    if (!product) {
      return;
    }
    loggedPrice.value = product.price_log_price_raw;
  }

  // Fetch ERP price
  async function fetchErpPrice(productId) {
    const payload = {
      products: {
        [productId]: 1
      }
    };

    try {
      $.ajax({
        type: "POST",
        url: "/ajax.php?mode=external_pricelists",
        data: payload,
        success: (data) => {
          const prices = data?.products?.[0]?.prices;
          const product = data?.products?.[0];

          if (!prices || prices.length === 0) return;
          
          const taxMultiplier = props.showVat ? productTax.value : 1;

          if (prices.length > 1) {
            // Sort prices by price ascending
            const sorted = [...prices].sort((a, b) => a.price - b.price);
            const [low, high] = sorted;
            priceRaw.value = high.price * taxMultiplier;
            // Set raw value directly - Vue component will format based on currency prop
            price.value = priceRaw.value;
            priceSpecialRaw.value = low.price * taxMultiplier;
            // Set raw value directly - Vue component will format based on currency prop
            priceSpecial.value = priceSpecialRaw.value;
          } else {
            // Only one price
            const priceObj = prices[0];
            const calculatedPrice = priceObj.price * taxMultiplier;
            priceRaw.value = calculatedPrice;
            // Set raw value directly - Vue component will format based on currency prop
            price.value = calculatedPrice;
          }
        }
      });
    } catch (error) {
      console.error("ERP price fetch failed:", error);
    }
  }

  async function fetchErpPricesForVariants(variantIds) {
    if (!Array.isArray(variantIds) || variantIds.length === 0) return;
    const payload = {
      products: Object.fromEntries(variantIds.map(id => [id, 1]))
    };

    try {
      const response = await new Promise((resolve, reject) => {
        $.ajax({
          type: "POST",
          url: "/ajax.php?mode=external_pricelists",
          data: payload,
          success: function (data) {
            resolve(data);
          },
          error: function (xhr, status, error) {
            console.error("AJAX error:", {
              status: status,
              error: error,
              responseText: xhr.responseText
            });
            reject(error);
          }
        });
      });

      const prices = (response?.products || []).map(product => {
        const price = product?.prices?.[0]?.price;
        return price != null ? { id: product.id, price } : null;
      }).filter(Boolean);

      if (prices.length > 0) {
        const cheapest = prices.reduce((min, p) => (p.price < min.price ? p : min));

        const calculatedApiPrice = props.showVat
        ? cheapest.price * productTax.value
        : cheapest.price;

        if (calculatedApiPrice >= priceRaw.value) {
          priceRaw.value = calculatedApiPrice;
          // Set raw value directly - Vue component will format based on currency prop
          price.value = calculatedApiPrice;
        } else {
          priceSpecialRaw.value = calculatedApiPrice;
          // Set raw value directly - Vue component will format based on currency prop
          priceSpecial.value = calculatedApiPrice;
        }
      }

    } catch (error) {
      console.error("Failed to fetch ERP prices for variants:", error);
    }
  }

  function b2cFromPrice() {
    const raw = cheapestVariantPriceRaw.value;

    if (typeof raw === 'number' && !isNaN(raw)) {

      priceRaw.value = raw;
      price.value = window.VendreCurrencyFormatter(raw);

    } else {
      console.warn('Invalid or missing cheapestVariantPriceRaw:', raw);
    }
  }

  // only fetch price logs if on page with single product NOT for listing or multiple product pages.
  if (priceSpecial.value && handlePriceLog.value) {
    getLoggedPrice();
  }

  if (priceSpecial.value && !handlePriceLog.value) {
    const product = priceStore.loggedPrices && priceStore.findLoggedPrice(props.productData.id);
    loggedPrice.value = product && product.price_log_price_raw;
  }

  // watch when loggedPrices is updated in the store and update product price if there is a logged price
  watch(loggedPrices, (newValue, oldValue) => {
    if (newValue) {
      if (priceSpecial.value && !props.handlePriceLogUpdate) {
        let productToUpdate = priceStore.loggedPrices.find((item) => {
          return item.product_id == props.productData.id
        });
        if (productToUpdate) {
          loggedPrice.value = productToUpdate.price_log_price_raw;
        }
      }
    }
  });

  const productTax = computed(() => {
    return (tax.value / 100) + 1;
  });
  
  const discountPrice = computed(() => {
    if (priceSpecialRaw.value) {
      return priceSpecial.value;
    }
  });

  const discountPriceRaw = computed(() => {
    if (priceSpecialRaw.value) {
      return priceSpecialRaw.value;
    }
  });

  const displayRedPrice = computed(() => {
    if (loggedPrice.value) {
      return priceSpecialRaw.value < loggedPrice.value;
    }
    return priceSpecialRaw.value < priceRaw.value;
  });


  const regularPrice = computed(() => {
    if (loggedPrice.value) {
      // Return raw value - Vue component will format based on currency prop
      return loggedPrice.value;
    } else {
      if (!intialPriceLoaded.value) {
        return '';
      }

      if (productConfiguratorPrice.value !== null) {
        return productConfiguratorPrice.value;
      }

      if (pricePerUnit.value) {
        return pricePerUnit.value;
      } else {
        return price.value;
      }
    }
  });

  const discountPercentage = computed(() => {
    let discount = null;

    if (loggedPrice.value) {
      const discountDecimals = (loggedPrice.value - priceSpecialRaw.value ) / loggedPrice.value;
      discount = Math.round(discountDecimals * 100);
    } else if (priceDiscountPercentage.value) {
      discount = priceDiscountPercentage.value;
    }

    return discount ? `(-${discount}%)` : null;
  });

  const vatText = computed(() => {
    if (!props) {
      return '';
    }

    if (props.showVat) {
      return $translate('EXCL_VAT') + ' ' + tooltipPrice('exvat');
    }

    return $translate('INCL_VAT') + ' ' + tooltipPrice('incvat');
  });

  const handleUpdatedProductConfiguratorPrice = (event) => {
    productConfiguratorPrice.value = event.detail.price;
  }

  function tooltipPrice(type) {
    let finalPrice;
    let productPrice;

    if (productConfiguratorPrice.value) {
      // Product configurator returns price as string - hence this conversion to float.
      const pattern = /[-,0-9]+/
      let productConfiguratorPriceRaw = productConfiguratorPrice.value.match(pattern)[0];
      productConfiguratorPriceRaw = productConfiguratorPriceRaw.replace(',', '.');
      productPrice = parseFloat(productConfiguratorPriceRaw);
    } else {
      productPrice = discountPriceRaw.value ? priceSpecialRaw.value : priceRaw.value;
    }

    if (type == 'incvat') {
      finalPrice = productPrice * productTax.value;
    } else {
      finalPrice = productPrice / productTax.value;
    }

    // Return raw value - Vue component will format based on currency prop
    return finalPrice;
  }

  const regularPriceRaw = computed(() => {
    if (loggedPrice.value) {
      return loggedPrice.value;
    } else if (productConfiguratorPrice.value !== null) {
      const pattern = /[-,0-9]+/
      let raw = productConfiguratorPrice.value.match(pattern)[0];
      raw = raw.replace(',', '.');
      return parseFloat(raw);
    } else if (pricePerUnit.value) {
      return priceRaw.value;
    } else {
      return priceRaw.value;
    }
  });

  const comparisonPrice = computed(() => {
    if (comparisonFactor.value == null) {
      comparisonFactor.value = 1
    }

    let regularPrice = priceRaw.value * comparisonFactor.value;

    if (priceSpecialRaw.value) {
      regularPrice = priceSpecialRaw.value * comparisonFactor.value;
    }

    // Return raw value - Vue component will format based on currency prop
    return regularPrice;
  });

  async function getPrice(productId) {
    let formElements = null;

    if (productId) { // categories listing
      formElements = document.querySelector(`form[name=product_${productId}]`);
    } else { // product page
      formElements = document.querySelector('form[name=add_product]');
    }

    const formData = new FormData(formElements);
    let serializedFormData = new URLSearchParams(formData).toString();

    if (
        formData.get('quantity') === undefined || formData.get('quantity') === null ||
        formData.get('products_id') === undefined || formData.get('products_id') === null
    ) {
      return;
    }

    fetch(`/ajax.php?mode=getProductPrice&hide_ajax_loader=true&${serializedFormData}`, {
      headers: {
        'Accept': 'application/json',
      },
      method: 'POST'
    })
      .then((response) => response.json())
      .then((data) => {
        let price = null;

        if (window.vendre_config.product.show_total_final_price) {
          if (data.total_final_price_raw) {
            price = data.total_final_price_raw;
          }
        } else {
          if (data.final_price_per_unit) {
            price = data.final_price_per_unit;
          } else if (data.final_price_raw) {
            price = data.final_price_raw;
          }
        }

        if (discountPrice.value) {
          priceSpecialRaw.value = price;
          // Set raw value directly - Vue component will format based on currency prop
          priceSpecial.value = price;
          
          const priceDiff = priceRaw.value - priceSpecialRaw.value;
          priceDiscountPercentage.value = Math.round((priceDiff / priceRaw.value) * 100);
        } else {
          priceRaw.value = price;
          // Set raw value directly - Vue component will format based on currency prop
          if (pricePerUnit.value) {
            pricePerUnit.value = price;
          } else {
            price.value = price;
          }
        }
      })
      .catch(error => {
        //
      });
  };
  
  return {
    getPrice,
    vatText,
    intialPriceLoaded,
    price,
    handleUpdatedProductConfiguratorPrice,
    discountPrice,
    discountPriceRaw,
    finalPriceExVatRaw,
    regularPrice,
    discountPercentage,
    displayRedPrice,
    comparisonPrice,
    priceRaw,
    fetchErpPrice,
    fetchErpPricesForVariants,
    cheapestVariantPrice,
    b2cFromPrice
  }
}